/* Campaign UI and lifecycle only. Never creates a discount or changes the cart. */
(() => {
  'use strict';
  if (window.klikCampaign) return;
  const source = document.querySelector('[data-kc-config]');
  if (!source) return;
  let config;
  try { config = JSON.parse(source.textContent); } catch (_) { return; }
  // startMs / endMs are the server's own instants (snippets/klik-money, from the theme settings), so the browser and the
  // server agree to the second whatever format the settings text uses; the ISO strings are the fallback.
  const instant = (ms, text) => (Number.isFinite(ms) && ms > 0 ? ms : Date.parse(text));
  const start = instant(config.startMs, config.start);
  const end = config.endMs === null || (config.endMs === undefined && config.end === null) ? Infinity : instant(config.endMs, config.end);
  const nationalDayStart = Date.parse('2026-09-23T00:00:00+03:00');
  const nationalDayEnd = Date.parse('2026-09-24T00:00:00+03:00');
  const valid = Number.isFinite(start) && (end === Infinity || Number.isFinite(end)) && end > start && /^[A-Za-z0-9_-]{3,32}$/.test(config.code || '') && Number(config.percent) > 0 && Number(config.percent) <= 100;
  const copyTimers = new WeakMap();
  let timer;
  let lastState = '';

  function state(now = Date.now()) {
    if (!valid) return 'draft';
    if (now >= end) return 'expired';
    if (config.enabled !== true) return 'draft';
    return now < start ? 'scheduled' : 'active';
  }
  function arabic() {
    return /^ar(?:-|$)/i.test(document.documentElement.lang || '');
  }
  function copyText(element, en, ar) {
    if (!element) return;
    element.setAttribute('data-klik-i18n-en', en);
    element.setAttribute('data-klik-i18n-ar', ar);
    element.textContent = arabic() ? ar : en;
  }
  function destination(override) {
    const nativeRoot = window.Shopify?.routes?.root || '/';
    const fallback = new URL(nativeRoot.replace(/\/?$/, '/') + 'collections/all', location.origin);
    let url;
    try { url = new URL(override || config.destination || fallback.href, location.origin); } catch (_) { url = fallback; }
    // The destination is always a local storefront route, never a payment or external URL.
    if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?collections\//.test(url.pathname)) url = fallback;
    const current = new URL(location.href);
    const preview = window.Shopify?.theme?.role === 'unpublished' ? String(window.Shopify.theme.id) : current.searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const view = current.searchParams.get('view');
    const design = view === 'regular' || view === 'national-day' ? view : current.searchParams.get('klik_design');
    // Preserve an explicit review choice; automatic National Day visits must expire naturally.
    if (design === 'regular' || design === 'national-day') url.searchParams.set('klik_design', design);
    return url;
  }
  function href(currentState, override) {
    const target = destination(override);
    if (currentState !== 'active') return target.href;
    const link = new URL('/discount/' + encodeURIComponent(config.code), location.origin);
    link.searchParams.set('redirect', target.pathname + target.search + target.hash);
    return link.href;
  }
  function refresh() {
    clearTimeout(timer);
    const currentState = state();
    const now = Date.now();
    // Wave 2 (WF-4): a page left open over the start instant applies the code too (once, when the state turns active).
    if (lastState && lastState !== 'active' && currentState === 'active') setTimeout(() => autoApply(), 0);
    lastState = currentState;
    const shoppingURL = href(currentState);
    document.querySelectorAll('[data-kc-national-today]').forEach(element => {
      element.hidden = now < nationalDayStart || now >= nationalDayEnd;
    });
    document.querySelectorAll('[data-kc-component]').forEach(component => {
      component.dataset.kcState = currentState;
      component.querySelectorAll('[data-kc-show]').forEach(element => { element.hidden = !element.dataset.kcShow.split(' ').includes(currentState); });
      component.querySelectorAll('[data-kc-redeem]').forEach(link => { link.href = link.dataset.kcDestination ? href(currentState, link.dataset.kcDestination) : shoppingURL; });
      component.querySelectorAll('[data-kc-apply-current]').forEach(link => {
        const current = new URL(location.href);
        if (!/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?products\/[^/]+$/.test(current.pathname)) return;
        const target = new URL(current.pathname, location.origin);
        ['variant','preview_theme_id','klik_design','view'].forEach(key => {
          if (current.searchParams.has(key)) target.searchParams.set(key,current.searchParams.get(key));
        });
        const discount = new URL('/discount/' + encodeURIComponent(config.code), location.origin);
        discount.searchParams.set('redirect',target.pathname + target.search);
        link.href = discount.href;
      });
      // PACKAGES §1.13 (one action per block): while the code is applied for the shopper (autoApply), there is nothing
      // to copy, so the band keeps only its own link; the copy button shows only when the code must be entered.
      component.querySelectorAll('[data-kc-copy]').forEach(button => { button.hidden = !valid || currentState === 'expired' || config.autoApply === true; });
      if (currentState === 'expired') {
        component.querySelectorAll('[data-kc-copy-status]').forEach(status => copyText(status, '', ''));
      }
    });
    // One boundary timer, no polling or animation loop. Reconcile again on tab return.
    if (valid && !document.hidden && currentState !== 'expired') {
      const boundaries = [config.enabled === true && now < start ? start : end];
      if (document.querySelector('[data-kc-national-today]')) {
        if (now < nationalDayStart) boundaries.push(nationalDayStart);
        if (now < nationalDayEnd) boundaries.push(nationalDayEnd);
      }
      const boundary = Math.min(...boundaries);
      timer = setTimeout(refresh, Math.min(2147483647, Math.max(1, boundary - now + 30)));
    }
    document.dispatchEvent(new CustomEvent('klik:campaign:refresh', { detail: { state: currentState } }));
    return currentState;
  }
  function selectCode(node) {
    if (!node || !window.getSelection || !document.createRange) return;
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  }
  async function copy(button) {
    const component = button.closest('[data-kc-component]');
    const status = component?.querySelector('[data-kc-copy-status]');
    const label = button.querySelector('[data-kc-copy-label]');
    if (!component || button.disabled || !valid || refresh() === 'expired') return;
    clearTimeout(copyTimers.get(button));
    button.disabled = true;
    delete button.dataset.kcResult;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(config.code);
      if (refresh() === 'expired') return;
      button.dataset.kcResult = 'copied';
      copyText(label, 'Copied', 'تم النسخ');
      copyText(status, state() === 'draft' ? 'Code copied. This preview offer is not active.' : state() === 'scheduled' ? 'Code copied. Use it once the offer begins.' : (config.autoApply === true ? 'Code copied. It is also applied for you before payment.' : 'Code copied. Paste it at checkout to apply your discount.'), state() === 'draft' ? 'تم نسخ الكود. عرض المعاينة غير مفعّل.' : state() === 'scheduled' ? 'تم نسخ الكود. استخدمه عند بدء العرض.' : (config.autoApply === true ? 'تم نسخ الكود. ويُطبَّق لك تلقائيًا قبل الدفع.' : 'تم نسخ الكود. أضفه عند الدفع لتطبيق الخصم.'));
      document.dispatchEvent(new CustomEvent('klik:campaign:copied', { detail: { button } }));
    } catch (_) {
      selectCode(component.querySelector('[data-kc-code]'));
      copyText(status, 'Automatic copy is unavailable. Select the code and copy it manually.', 'تعذّر النسخ التلقائي. حدّد الرمز وانسخه يدويًا.');
    } finally {
      button.disabled = false;
      copyTimers.set(button, setTimeout(() => {
        delete button.dataset.kcResult;
        copyText(label, 'Copy code', 'نسخ الكود');
      }, 3000));
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-kc-copy]');
    if (button) copy(button);
    // A page left open past the deadline must never send a stale redemption link.
    if (event.target.closest('[data-kc-redeem]')) refresh();
  }, true);
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('recommendations:loaded', refresh);
  window.addEventListener('pageshow', refresh);
  // K-013: while the campaign runs (and its auto_apply flag is on), apply the code once per session from whatever page
  // the visit starts on, not only from the links that pass through /discount/CODE, so the price shown is the price
  // charged. A code that is already set (the shopper's own, a partner's) is kept while Shopify applies it to the cart;
  // one Shopify does not apply gives way to the running code (C-VERIFY M2). Runs when the page is idle, outside the
  // theme editor.
  // Refine 1 (bugs-cart critic, 21 Oct): the session counts as done only once the CART carries the code. An empty cart
  // cannot show it yet, so the session is marked "pending" (no further requests on later pages) and the cart is checked
  // again after the first add (klik:cart-added, Motion's cart:updated); a cart that still lacks the code gets the code
  // applied once more. Then klik:campaign:applied (detail.verified) tells the cart surfaces to read the cart again.
  const appliedKey = 'klik-campaign-applied:' + config.code;
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const sameCode = (value) => String(value || '').toUpperCase() === String(config.code || '').toUpperCase();
  // Wave 2 (WF-4): an ended KLIK campaign code (LAMMA10 still in a cart or in the discount_code cookie on 19 Nov) never
  // blocks the running one. Any other code is kept only while Shopify applies it to the cart (cartState, C-VERIFY M2).
  const ownCodes = (Array.isArray(config.campaignCodes) ? config.campaignCodes : []).map((c) => String(c || '').toUpperCase());
  const retired = (value) => { const v = String(value || '').toUpperCase(); return v !== '' && !sameCode(v) && ownCodes.includes(v); };
  const flag = () => { try { return sessionStorage.getItem(appliedKey) || ''; } catch (_) { return ''; } };
  const setFlag = (value) => { try { sessionStorage.setItem(appliedKey, value); } catch (_) {} };
  const verified = () => { const value = flag(); return value !== '' && value !== 'pending'; };
  let applying = false, reapplied = false;
  function applyCode() {
    return fetch(root + 'discount/' + encodeURIComponent(config.code), { credentials: 'same-origin', redirect: 'manual', cache: 'no-store' }).catch(() => null);
  }
  function readCart() {
    const shared = window.KlikCart?.get;
    const request = shared ? Promise.resolve(shared.call(window.KlikCart, { fresh: true })) : fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(response => (response.ok ? response.json() : null));
    return request.catch(() => null);
  }
  // applied | other (another code Shopify applies to the cart: kept) | missing (lines, no applicable code) | empty | unknown
  function cartState(cart) {
    if (!cart || typeof cart !== 'object') return 'unknown';
    const codes = (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).filter(entry => entry && entry.applicable !== false && !retired(entry.code)).map(entry => entry.code);
    if (codes.some(sameCode)) return 'applied';
    if (codes.some(Boolean)) return 'other';
    return Number(cart.item_count) > 0 ? 'missing' : 'empty';
  }
  function settle(result) {
    if (result === 'applied' || result === 'other') {
      setFlag(String(Date.now()));
      if (result === 'applied') document.dispatchEvent(new CustomEvent('klik:campaign:applied', { detail: { code: config.code, verified: true } }));
    } else if (result === 'empty' || result === 'missing') setFlag('pending');
  }
  function check(applyFirst) {
    if (applying || config.autoApply !== true || state() !== 'active' || window.Shopify?.designMode || verified()) return Promise.resolve();
    applying = true;
    return (applyFirst ? applyCode() : Promise.resolve())
      .then(readCart)
      .then(cart => {
        const result = cartState(cart);
        if (result !== 'missing' || reapplied) return result;
        reapplied = true;
        return applyCode().then(readCart).then(cartState);
      })
      .then(settle)
      .catch(() => {})
      .finally(() => { applying = false; });
  }
  function autoApply() {
    if (config.autoApply !== true || state() !== 'active' || window.Shopify?.designMode || flag() !== '') return;
    const cookie = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
    let current = '';
    try { current = cookie ? decodeURIComponent(cookie[1]) : ''; } catch (_) { current = cookie ? cookie[1] : ''; }
    // C-VERIFY M2: another code in the cookie (a partner's, or a KLIK code without a campaign entry, e.g. KLIK1111 or
    // WHITEFIRST after its end) is not trusted blindly. Only the cart is read: kept if Shopify applies it there, replaced
    // once if not (after the first add when the cart is still empty).
    check(!current || retired(current));
  }
  // After an add: the first cart of the session, or a cart the code did not reach.
  const afterAdd = () => { if (!verified()) setTimeout(() => check(false), 400); };
  document.addEventListener('klik:cart-added', afterAdd);
  document.addEventListener('cart:updated', afterAdd);
  window.klikCampaign = Object.freeze({ refresh, state, autoApply });
  refresh();
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
  const later = () => idle(autoApply, { timeout: 4000 });
  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later, { once: true });
})();
